import React, { useState, useEffect } from 'react';
import { Microscope, Award } from 'lucide-react';
import { useI18n } from '../hooks/useI18n';
import { getFileUrl } from '../utils/assetUrlHelper';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Brand gradients for fallbacks
const GRADIENTS = [
  'from-[#0E6D2B] to-[#21A366]',  // Green
  'from-[#1565C0] to-[#42A5F5]',  // Blue
  'from-[#F57C00] to-[#FFB74D]',  // Orange
  'from-[#7B1FA2] to-[#BA68C8]',  // Purple
];

// Default key research areas
const defaultResearch = [
  {
    id: '1',
    title: 'SMART BREEDING FOR CLIMATE RESILIENCE',
    excerpt: 'Advanced breeding programs for climate-adapted varieties',
    image_url: '',
    link_url: '/research/climate-resilience',
    priority: 100,
  },
  {
    id: '2',
    title: 'DIGITAL ORCHARDS, SUSTAINABLE HARVEST AND RESOURCE CONSERVATION',
    excerpt: 'Technology-driven sustainable agriculture practices',
    image_url: '',
    link_url: '/research/digital-orchards',
    priority: 90,
  },
  {
    id: '3',
    title: 'PROTECTION TO PREVENTION ECO FRIENDLY PEST MANAGEMENT',
    excerpt: 'Integrated pest management solutions',
    image_url: '',
    link_url: '/research/pest-management',
    priority: 80,
  },
  {
    id: '4',
    title: 'INNOVATION IN VALUE CHAIN RESEARCH',
    excerpt: 'Post-harvest technology and value addition',
    image_url: '',
    link_url: '/research/value-chain',
    priority: 70,
  },
];

/**
 * KeyResearchGrid Component
 * Displays 4 key research areas in a responsive grid
 * Features: priority ordering, image fallbacks, clickable cards, responsive design
 */
export default function KeyResearchGrid() {
  const { t } = useI18n();
  const [research, setResearch] = useState(defaultResearch);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchKeyResearch();
  }, []);

  const fetchKeyResearch = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/research/key`);
      if (response.data && response.data.length > 0) {
        // Ensure we always have exactly 4 items
        const items = response.data.slice(0, 4);
        // Pad with defaults if less than 4
        while (items.length < 4) {
          items.push(defaultResearch[items.length]);
        }
        setResearch(items);
      }
    } catch (error) {
      console.error('Failed to fetch key research:', error);
      // Keep default research on error
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (item) => {
    if (item.link_url) {
      window.location.href = item.link_url;
    }
  };

  return (
    <section className="py-8 md:py-14 bg-white" data-testid="key-research-grid">
      <div className="container-cish">
        {/* Section heading */}
        <div className="section-heading">
          <Microscope className="section-heading-icon w-5 h-5 md:w-6 md:h-6" />
          <h2 className="section-heading-text">{t('keyResearch')}</h2>
        </div>

        {/* Research cards grid - Always show 4 slots */}
        <div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
          role="list"
        >
          {research.map((item, index) => {
            const gradientClass = GRADIENTS[index % GRADIENTS.length];
            const isFeatured = index === 0; // Top priority card is featured
            
            return (
              <div
                key={item.id || index}
                role="link"
                aria-label={`${item.title}${isFeatured ? ' (Featured)' : ''}`}
                tabIndex={0}
                onClick={() => handleCardClick(item)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleCardClick(item);
                  }
                }}
                className="group cursor-pointer h-full flex flex-col bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#0E6D2B] focus:ring-offset-2"
                data-testid={`research-card-${index}`}
              >
                {/* Top Image Area */}
                <div className="relative aspect-[4/3] overflow-hidden">
                  {/* Featured Badge */}
                  {isFeatured && (
                    <div className="absolute top-2 right-2 z-10 bg-[#0E6D2B] text-white text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1 shadow-lg">
                      <Award className="w-3 h-3" />
                      <span>FEATURED</span>
                    </div>
                  )}

                  {/* Image or Gradient */}
                  {item.image_url && item.image_url.trim().length > 0 ? (
                    <>
                      <img
                        src={getFileUrl(item.image_url)}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                        onError={(e) => {
                          // On error, hide image and show gradient fallback
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      {/* Gradient fallback (hidden initially) */}
                      <div 
                        className={`absolute inset-0 bg-gradient-to-br ${gradientClass} items-center justify-center hidden`}
                        style={{ display: 'none' }}
                      >
                        <Microscope className="w-12 h-12 text-white/40" />
                      </div>
                    </>
                  ) : (
                    // No image - show gradient with icon
                    <div className={`w-full h-full bg-gradient-to-br ${gradientClass} flex items-center justify-center`}>
                      <Microscope className="w-12 h-12 text-white/40" />
                    </div>
                  )}
                </div>

                {/* White Footer Card with Title and Excerpt */}
                <div className="flex-1 flex flex-col p-4 bg-white">
                  <h3 className="text-sm font-bold uppercase text-gray-900 leading-tight line-clamp-3 mb-2">
                    {item.title}
                  </h3>
                  {item.excerpt && item.excerpt.trim().length > 0 && (
                    <p className="text-xs text-gray-500 line-clamp-2">
                      {item.excerpt}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
